// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { NetworkService } from '@core/services/network.service';
import { OfflineFreshnessService } from '@core/offline/offline-freshness.service';
import { SaraIconComponent } from '@shared/ui/sara-icon/sara-icon.component';

/**
 * EF-1.2 — the always-visible connectivity indicator: discreet while online,
 * explicit while offline, and a click opens the state details (status, last
 * server contact, pending actions). Rendered by the top bar behind the
 * OfflineMode flag. Pure chrome over NetworkService + OfflineFreshnessService
 * — no state of its own.
 *
 * B3 (EF-4.2 honesty): while vault-served content is on screen, the offline
 * label carries the capture time of the STALEST serve of this offline episode
 * ("Offline — data from 14:32"), and the menu spells it out — no stale data
 * presented as current, with zero new chrome (deliberate: the chip is THE
 * indicator, and Product-side UI exploration is in flight).
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'connectivity-status',
  templateUrl: './connectivity-status.component.html',
  styleUrls: ['./connectivity-status.component.scss'],
  imports: [MatMenuModule, SaraIconComponent],
})
export class ConnectivityStatusComponent {
  readonly network = inject(NetworkService);
  readonly freshness = inject(OfflineFreshnessService);

  /**
   * Human form of NetworkService.lastServerContactAt. Until the sync engine
   * exists this is the honest value behind EF-1.2's "last successful sync":
   * the last time the server answered anything.
   */
  readonly lastContactLabel = computed(() => {
    const t = this.network.lastServerContactAt();
    return t === null ? 'Not yet this session' : new Date(t).toLocaleTimeString();
  });

  /**
   * The offline chip label: bare "Offline" until the vault serves something,
   * then "Offline — data from <time of the stalest serve>" (EF-4.2 promises
   * no more than the stalest thing on screen).
   */
  readonly offlineLabel = computed(() => {
    const oldest = this.freshness.oldestServedAt();
    return oldest === null ? 'Offline' : `Offline — data from ${new Date(oldest).toLocaleTimeString()}`;
  });

  /** Menu row value: explicit capture stamp of the vault-served content. */
  readonly servedDataLabel = computed(() => {
    const oldest = this.freshness.oldestServedAt();
    return oldest === null ? null : new Date(oldest).toLocaleString();
  });

  /**
   * Pending-action count — hardcoded until the offline action queue exists
   * (plan phase C), at which point this becomes the queue's live count.
   */
  readonly pendingActions = 0;
}
